import { motion } from "framer-motion";
import PageHero from "@/components/layout/PageHero";
import CTA from "@/components/sections/CTA";
import { IMAGES } from "@/lib/constants";

const PILLARS = [
    {
        n: "01",
        label: "Boundaries",
        title: "What Carbonis actually measures.",
        body: "Carbonis produces a confidence-weighted composite score covering physical, transition, regulatory, financial, and operational climate risk. Boundaries are set per engagement — organisational, operational-control, or equity-share — and are explicit in every dossier."
    },
    {
        n: "02",
        label: "Data Sources",
        title: "Where the numbers come from.",
        body: "Primary sources: client-provided utility, fuel, and production data (verified against invoices where available). Secondary sources: India CEA National Grid Emission Factor v20, IPCC AR6 GWP values, GHG Protocol emission factor databases, DEFRA-BEIS 2024, and PCAF asset-class methodologies. Every input carries a source tag and confidence weight."
    },
    {
        n: "03",
        label: "Emission Factors",
        title: "The factors we use, and why.",
        body: "Scope 1 combustion: IPCC AR6 + India-specific fuel factors. Scope 2 electricity: India CEA National Grid EF v20 (0.716 kgCO₂e/kWh) as default; state-level EF used where available. Scope 3: GHG Protocol categories 1-15 mapped to sector-specific defaults, refined with buyer-supplied primary data where the supplier provides it."
    },
    {
        n: "04",
        label: "Confidence Weighting",
        title: "Why every score comes with a confidence band.",
        body: "The Carbonis Score is not a point estimate. It is a probability-weighted composite computed from data completeness (0-100%), source primacy (primary / secondary / modelled), and methodological alignment. A 44/100 score at 82% confidence and a 44/100 score at 42% confidence are treated as different objects."
    },
    {
        n: "05",
        label: "Attribution",
        title: "PCAF-aligned financed emissions.",
        body: "For banking clients, financed emissions follow PCAF v2 methodology (attribution based on outstanding exposure ÷ enterprise value including cash for listed corporates; ÷ total balance-sheet value for unlisted). Data quality scores 1-5 are surfaced alongside the attribution."
    },
    {
        n: "06",
        label: "Regulatory Alignment",
        title: "The frameworks we map to.",
        body: "RBI Draft Framework on Climate-Related Financial Risks · SEBI BRSR Core · EU CBAM · GHG Protocol Corporate + Value Chain · ISO 14064-1 · IFRS S2 · TCFD · PCAF Global GHG Accounting Standard. Every dossier is exportable in the format the receiving supervisor or buyer expects."
    },
    {
        n: "07",
        label: "Versioning",
        title: "Methodology as code, not marketing.",
        body: "Every methodology change is versioned, dated, and back-tested. Prior scores remain reproducible under their original methodology. Audit trail is exportable on request — supervisory reviewers can walk any score back to its inputs."
    },
    {
        n: "08",
        label: "Limitations",
        title: "What Carbonis does not claim.",
        body: "Carbonis is not a verified assurance provider. Where third-party assurance is required (SEBI BRSR Core, EU CBAM verifier), Carbonis produces the dossier — a licensed assurance provider signs it. We do not produce carbon credits, do not act as a registry, and do not warrant abatement outcomes we have not implemented."
    }
];

export default function Methodology() {
    return (
        <div data-testid="page-methodology">
            <PageHero
                title={<>The methodology, <em className="text-gold not-italic font-light">on the record.</em></>}
                lead="Carbonis is engineered for the moment an auditor, a supervisor, or a credit committee walks in. This page is the standing answer to how the numbers are produced — and what they mean."
                image={IMAGES.satellite}
            />

            <section className="relative py-24 lg:py-36 border-t border-navy/10">
                <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-20">
                        <div className="lg:col-span-6">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-10 h-px bg-gold"></div>
                                <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">
                                    Methodology
                                </span>
                            </div>
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em] text-navy">
                                Every score, <em className="text-gold not-italic font-light">walked back to its inputs.</em>
                            </h2>
                        </div>
                        <div className="lg:col-span-5 lg:col-start-8 lg:self-end">
                            <p className="text-navy/60 text-lg leading-relaxed">
                                Eight positions on how Carbonis produces, weights, and defends the numbers on your dossier.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-16 lg:gap-24">
                        {PILLARS.map((p) => (
                            <motion.div
                                key={p.n}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.8 }}
                                className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"
                                data-testid={`methodology-pillar-${p.n}`}
                            >
                                <div className="lg:col-span-3">
                                    <div className="chapter-num text-[100px] md:text-[140px] leading-[0.9] text-gold/85">{p.n}</div>
                                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-navy/50 mt-1">
                                        {p.label}
                                    </div>
                                </div>
                                <div className="lg:col-span-8 lg:col-start-5 lg:pt-3">
                                    <h3 className="font-serif text-3xl md:text-4xl leading-[1.1] tracking-[-0.01em] text-navy">
                                        {p.title}
                                    </h3>
                                    <p className="mt-5 text-navy/65 text-[15.5px] lg:text-lg leading-relaxed max-w-[720px]">
                                        {p.body}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <CTA />
        </div>
    );
}
